import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { environment } from '../../../environments/environment';

export interface IFxRate {
  currencyCode: string;
  /** Units of currencyCode per 1 EUR (ECB reference rate). */
  rate: number;
  cobDate: string;
}

/** Snapshot stored with a task so a converted amount stays fixed and can be explained later. */
export interface IFxConversion {
  targetCurrency: string;
  cobDate: string | null;
  originalTotals: Record<string, number>;
  ratesPerEur: Record<string, number>;
}

export interface IAmountSummary {
  amount: number;
  currencyCode: string;
  conversion?: IFxConversion;
}

export const FX_TARGET_CURRENCY = 'GBP';

/**
 * Converts between currencies using ECB reference rates held in MarketData.
 * All rates are quoted against EUR, so every conversion goes source -> EUR -> target.
 */
@Injectable()
export class FxRateService {
  private ratesPerEur = new Map<string, number>([['EUR', 1]]);
  private cobDate: string | null = null;

  constructor(private readonly api: ApiService) { }

  /** Loads the latest rates; emits true on success and false (without throwing) if they are unavailable. */
  loadRates(): Observable<boolean> {
    return this.api.fetch<IFxRate[]>(`${environment.taskManagerServiceEndPoint}/FxRates`).pipe(
      tap((rates: IFxRate[]) => this.setRates(rates)),
      map(() => true),
      catchError(() => of(false))
    );
  }

  setRates(rates: IFxRate[] | null | undefined): void {
    const next = new Map<string, number>([['EUR', 1]]);
    let latestCobDate: string | null = null;
    (rates ?? []).forEach(r => {
      const rate = Number(r?.rate);
      if (r?.currencyCode && rate > 0) {
        next.set(r.currencyCode.trim().toUpperCase(), rate);
        if (r.cobDate && (!latestCobDate || r.cobDate > latestCobDate)) {
          latestCobDate = r.cobDate;
        }
      }
    });
    this.ratesPerEur = next;
    this.cobDate = latestCobDate;
  }

  /** Returns null when a rate for either currency is missing, so callers can fall back rather than show a wrong total. */
  convert(amount: number, fromCurrency: string, toCurrency: string): number | null {
    if (fromCurrency?.toUpperCase() === toCurrency?.toUpperCase()) {
      return amount;
    }

    const from = this.ratesPerEur.get(fromCurrency?.toUpperCase());
    const to = this.ratesPerEur.get(toCurrency?.toUpperCase());
    if (!from || !to) {
      return null;
    }

    return (amount / from) * to;
  }

  /**
   * Totals loan rows by currency. One currency keeps its own currency; several are converted to GBP
   * using the currently loaded rates. Throws if a needed rate is missing, since a guessed total must never be stored.
   */
  summariseAmounts(rows: any[], defaultCurrency: string): IAmountSummary {
    const originalTotals: Record<string, number> = {};
    (rows ?? []).forEach(row => {
      const amount = Number(row?.amount);
      if (!isNaN(amount)) {
        const currency = (row?.currency || defaultCurrency).toUpperCase();
        originalTotals[currency] = this.round((originalTotals[currency] ?? 0) + amount);
      }
    });

    const currencies = Object.keys(originalTotals);
    if (currencies.length <= 1) {
      const currency = currencies[0] ?? defaultCurrency.toUpperCase();
      return { amount: originalTotals[currency] ?? 0, currencyCode: currency };
    }

    const usedRates: Record<string, number> = {};
    let gbpTotal = 0;
    for (const currency of currencies) {
      const converted = this.convert(originalTotals[currency], currency, FX_TARGET_CURRENCY);
      if (converted === null) {
        const loaded = [...this.ratesPerEur.keys()].filter(code => code !== 'EUR');
        throw new Error(
          `No exchange rate is available to convert ${currency} to ${FX_TARGET_CURRENCY} ` +
          `(rates loaded for ${loaded.length} currencies${loaded.length ? ': ' + loaded.join(', ') : ''}).`
        );
      }
      gbpTotal += converted;
      usedRates[currency] = this.ratesPerEur.get(currency) as number;
    }
    usedRates[FX_TARGET_CURRENCY] = this.ratesPerEur.get(FX_TARGET_CURRENCY) as number;

    return {
      amount: this.round(gbpTotal),
      currencyCode: FX_TARGET_CURRENCY,
      conversion: {
        targetCurrency: FX_TARGET_CURRENCY,
        cobDate: this.cobDate,
        originalTotals,
        ratesPerEur: usedRates,
      },
    };
  }

  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
